import { ArrowUpRight } from 'lucide-react';
import type { StreamLinks } from '@/types';
import { streamPlatformIcon } from '@/lib/simpleIcons';
import { SimpleIcon } from '@/components/icons/SimpleIcon';

function platformLabel(key: string): string {
  const k = key.toLowerCase().replace(/[\s_-]/g, '');
  if (k === 'apple' || k === 'applemusic') return 'Apple';
  if (k === 'youtube' || k === 'youtubemusic' || k === 'ytmusic') {
    return 'YouTube';
  }
  if (k === 'soundcloud') return 'SoundCloud';
  return key.charAt(0).toUpperCase() + key.slice(1);
}

export function hasStreamLinks(
  streamLinks?: StreamLinks | null | undefined,
): boolean {
  return Object.values(streamLinks || {}).some(Boolean);
}

export function StreamLinksList({
  streamLinks,
  variant = 'inline',
}: {
  streamLinks?: StreamLinks | null | undefined;
  /** `popover` = credit-style stacked rows with visible brand icons. */
  variant?: 'inline' | 'popover';
}) {
  const entries = Object.entries(streamLinks || {})
    .filter(([, href]) => Boolean(href))
    .sort(([a], [b]) =>
      platformLabel(a).localeCompare(platformLabel(b), undefined, {
        sensitivity: 'base',
      }),
    );
  if (!entries.length) return null;
  const isPopover = variant === 'popover';
  const iconSize = isPopover ? 16 : 14;

  return (
    <div
      className={
        isPopover
          ? 'stream-links-list stream-links-list--popover'
          : 'stream-links-list'
      }
    >
      {entries.map(([key, href]) => {
        const label = platformLabel(key);
        const icon = streamPlatformIcon(key);
        return (
          <a key={key} href={href} target="_blank" rel="noreferrer" title={label}>
            {icon ? (
              <SimpleIcon
                icon={icon}
                className="stream-link-icon"
                size={iconSize}
              />
            ) : (
              <span
                className="stream-link-icon stream-link-icon-fallback"
                aria-hidden
              >
                {label.charAt(0)}
              </span>
            )}
            <span className="stream-link-label">{label}</span>
            {!isPopover ? (
              <ArrowUpRight
                className="stream-link-external"
                size={11}
                aria-hidden
              />
            ) : null}
          </a>
        );
      })}
    </div>
  );
}

export function StreamLinksRow({
  streamLinks,
}: {
  streamLinks?: StreamLinks | null | undefined;
}) {
  if (!hasStreamLinks(streamLinks)) return null;

  return (
    <div className="stream-links">
      <span className="stream-links-label">Listen</span>
      <StreamLinksList streamLinks={streamLinks} />
    </div>
  );
}
